import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, ClipboardListIcon, CheckCircleIcon, AlertCircleIcon, AlertTriangleIcon } from 'lucide-react';
import { ServiceNowSubNav } from '../components/ServiceNowSubNav';
const SECURE_ON_PREMISE_PATH = '/services/servicenow/secure-on-premise';
const questions = ['Do you have a documented classification matrix identifying which data domains must sync between primary and secure instances, and which must not?', 'Is there a designated System of Record for every data domain that syncs, agreed on in writing?', 'Do you currently track your sync error or data discrepancy rate?', 'Is your sync process automated, rather than relying on manual re-keying by an admin?', 'Do you have a documented change management process for adding new data types to the sync pipeline?', 'Are your current sync patterns documented and referenced in your RMF/ATO package?', 'Do you have automated alerting when a sync fails or a record goes missing between instances?', 'Can you name, without checking, who is accountable when primary and secure data diverge?', 'Does your current SI or vendor team hold the clearances needed to actually work inside your secure environment?', 'Has a security review or audit flagged your cross-instance sync process in the past 12 months?'];
type Answer = 'yes' | 'no' | null;
interface RiskBand {
  level: string;
  range: string;
  color: string;
  bgColor: string;
  borderColor: string;
  icon: React.ReactNode;
  description: string;
}
function getRiskBand(noCount: number): RiskBand {
  if (noCount <= 2) return {
    level: 'Low Risk',
    range: '0–2 "No" answers',
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
    icon: <CheckCircleIcon className="h-8 w-8 text-green-500" data-id="element-1216" data-mp="1lutr7n0gyry5" />,
    description: 'Your governance is likely documented and holding up under scrutiny.'
  };
  if (noCount <= 5) return {
    level: 'Moderate Risk',
    range: '3–5 "No" answers',
    color: 'text-yellow-700',
    bgColor: 'bg-yellow-50',
    borderColor: 'border-yellow-200',
    icon: <AlertCircleIcon className="h-8 w-8 text-yellow-500" data-id="element-1217" data-mp="3g5thxzee9bpw" />,
    description: 'Gaps exist that are worth closing before they surface in an audit.'
  };
  return {
    level: 'High Risk',
    range: '6+ "No" answers',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: <AlertTriangleIcon className="h-8 w-8 text-red-500" data-id="element-1218" data-mp="3u1or3dizr8g3" />,
    description: 'Manual, undocumented sync at this scale is a matter of when, not if, something breaks or an ATO gets questioned.'
  };
}
export function CrossInstanceSyncChecklistPage() {
  const [answers, setAnswers] = useState<Answer[]>(Array(questions.length).fill(null));
  const [showResults, setShowResults] = useState(false);
  const answeredCount = answers.filter(a => a !== null).length;
  const allAnswered = answeredCount === questions.length;
  const noCount = answers.filter(a => a === 'no').length;
  const band = showResults ? getRiskBand(noCount) : null;
  const setAnswer = (index: number, value: Answer) => {
    setAnswers(prev => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };
  const handleScore = () => {
    if (allAnswered) setShowResults(true);
  };
  const handleReset = () => {
    setAnswers(Array(questions.length).fill(null));
    setShowResults(false);
  };
  return <div className="w-full" data-id="element-1219" data-mp="1ncwoiu7f3key">
      <ServiceNowSubNav data-id="element-1220" data-mp="1igo8zfpb5p79" />

      {/* ARTICLE */}
      <article className="section-white" data-id="element-1221" data-mp="1ihzblc70oqws">
        <div className="max-w-[760px] mx-auto px-5" data-id="element-1222" data-mp="1x5khfdva148e">
          {/* Back link */}
          <Link to={SECURE_ON_PREMISE_PATH} className="inline-flex items-center gap-2 text-nrs-blue font-semibold text-sm hover:text-nrs-orange transition-colors mb-8" data-id="element-1223" data-mp="1sec80hty5iv8">
            <ArrowLeftIcon className="h-4 w-4" data-id="element-1224" data-mp="14mx3ciep6pgt" />
            Back to Secure On-Premise
          </Link>

          {/* Category pill */}
          <div className="flex items-center gap-3 mb-4" data-id="element-1225" data-mp="2yjoev57s23h">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-nrs-blue" data-id="element-1226" data-mp="304i6ijpfh0a">
              <ClipboardListIcon className="h-3 w-3" data-id="element-1227" data-mp="bz81nnms9yfb" />
              Self-Assessment
            </span>
          </div>

          {/* Title */}
          <motion.h1 initial={{
          opacity: 0,
          y: 16
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} className="text-3xl md:text-4xl font-bold text-nrs-navy leading-tight mb-8" data-id="element-1228" data-mp="gqg9wgcdndqw">
            Is Your Cross-Instance Sync at Risk? A 10-Question Checklist
          </motion.h1>

          {/* Body */}
          <div className="border-t border-gray-200 pt-8" data-id="element-1229" data-mp="znhieb553vzl">
            <p className="text-gray-600 text-lg leading-relaxed mb-8" data-id="element-1230" data-mp="2wgu4vbhf3dxs">
              Answer honestly. Each "No" is a real risk indicator, not just a
              documentation gap.
            </p>

            <AnimatePresence mode="wait" data-id="element-1231" data-mp="2twea5dzxpcah">
              {!showResults ? <motion.div key="questions" initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} data-id="element-1232" data-mp="2txz534q5ux8v">
                  {/* Progress Bar */}
                  <div className="mb-8" data-id="element-1233" data-mp="2k48he21uyqu8">
                    <div className="flex items-center justify-between mb-2" data-id="element-1234" data-mp="2f89rub4sn2s8">
                      <span className="text-sm font-semibold text-gray-600" data-id="element-1235" data-mp="3cv0f53yknnjo">
                        {answeredCount} of {questions.length} answered
                      </span>
                      <span className="text-sm text-gray-400" data-id="element-1236" data-mp="37ucpalm2xcr1">
                        {Math.round(answeredCount / questions.length * 100)}%
                        complete
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden" data-id="element-1237" data-mp="1rifelsgkbc7g">
                      <motion.div className="h-full bg-nrs-orange rounded-full" animate={{
                    width: `${answeredCount / questions.length * 100}%`
                  }} transition={{
                    duration: 0.3
                  }} data-id="element-1238" data-mp="1ngkdop2jn0u7" />
                    </div>
                  </div>

                  {/* Questions */}
                  <ol className="space-y-4" role="list" data-id="element-1239" data-mp="1eyie7djtuwo6">
                    {questions.map((q, i) => {
                  const selected = answers[i];
                  return <motion.li key={i} initial={{
                    opacity: 0,
                    y: 16
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} transition={{
                    delay: i * 0.04
                  }} className="border border-gray-200 rounded-xl p-4" data-id="element-1240" data-mp="166zx90f24cu1" data-mpi={i}>
                          <div className="flex items-start gap-4 mb-3" data-id="element-1241" data-mp="118os32zpiqi5">
                            <span className="flex-shrink-0 flex items-center justify-center h-8 w-8 rounded-full bg-nrs-blue text-white font-heading font-bold text-sm" data-id="element-1242" data-mp="n7m4umwz7a5n">
                              {i + 1}
                            </span>
                            <span className="text-gray-700 text-base leading-relaxed pt-1" data-id="element-1243" data-mp="8lp1bx08gwq2">
                              {q}
                            </span>
                          </div>
                          <div className="flex gap-3 pl-12" data-id="element-1244" data-mp="hnogskvlnsqw">
                            <button type="button" aria-pressed={selected === 'yes'} onClick={() => setAnswer(i, 'yes')} className={`flex-1 py-2 rounded-lg border text-sm font-semibold transition-all ${selected === 'yes' ? 'border-green-500 bg-green-50 text-green-700' : 'border-gray-200 bg-white text-gray-600 hover:border-green-400 hover:bg-green-50/50'}`} data-id="element-1245" data-mp="cmilcfak1rdo">
                              Yes
                            </button>
                            <button type="button" aria-pressed={selected === 'no'} onClick={() => setAnswer(i, 'no')} className={`flex-1 py-2 rounded-lg border text-sm font-semibold transition-all ${selected === 'no' ? 'border-red-500 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-600 hover:border-red-400 hover:bg-red-50/50'}`} data-id="element-1246" data-mp="wcaz8vgix0mc">
                              No
                            </button>
                          </div>
                        </motion.li>;
                })}
                  </ol>

                  {/* Score Button */}
                  <div className="mt-8" data-id="element-1247" data-mp="2qog150p0mlac">
                    <button type="button" onClick={handleScore} disabled={!allAnswered} className={`w-full py-4 rounded-xl font-heading font-bold text-lg transition-all ${allAnswered ? 'bg-nrs-orange text-white hover:bg-amber-500 shadow-lg' : 'bg-gray-200 text-gray-400 cursor-not-allowed'}`} data-id="element-1248" data-mp="2qnees02jucdb">
                      {allAnswered ? 'See My Score →' : `Answer all questions to continue (${questions.length - answeredCount} remaining)`}
                    </button>
                  </div>
                </motion.div> : <motion.div key="results" initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-1249" data-mp="1a94zrwk75zz5">
                  {/* Score Display */}
                  <div className={`rounded-2xl border-2 ${band!.borderColor} ${band!.bgColor} p-8 text-center mb-6`} data-id="element-1250" data-mp="12rgm3w9qrq9j">
                    <div className="flex justify-center mb-4" data-id="element-1251" data-mp="xp7pmpxnu62o">{band!.icon}</div>
                    <div className="text-gray-500 text-sm mb-1" data-id="element-1252" data-mp="sr66y645c8rq">
                      Your "No" Answers
                    </div>
                    <div className="font-heading font-extrabold text-6xl text-nrs-navy mb-2" data-id="element-1253" data-mp="osgwalaht5bs">
                      {noCount}
                    </div>
                    <div className="text-gray-400 text-sm mb-3" data-id="element-1254" data-mp="ezrqa5xpojnu">
                      out of {questions.length}
                    </div>
                    <div className={`inline-block px-4 py-1.5 rounded-full font-heading font-bold text-sm ${band!.color} ${band!.bgColor} border ${band!.borderColor}`} data-id="element-1255" data-mp="a3azt531ht7l">
                      {band!.level}
                    </div>
                    <p className={`mt-4 text-sm leading-relaxed ${band!.color} max-w-lg mx-auto`} data-id="element-1256" data-mp="33sbn9nc574in">
                      {band!.description}
                    </p>
                  </div>

                  {/* Scoring guide */}
                  <div className="section-navy rounded-xl p-8 mb-6" data-id="element-1257" data-mp="3diejrxih30lq">
                    <h2 className="font-heading font-bold text-xl text-white mb-6" data-id="element-1258" data-mp="38j1yhh7921l9">
                      How the Scoring Works
                    </h2>
                    <div className="space-y-4" data-id="element-1259" data-mp="2ktzew3t21yo4">
                      {[{
                    range: '0–2 "No" answers',
                    label: 'Low risk',
                    detail: 'Your governance is likely documented and holding up under scrutiny.',
                    active: noCount <= 2
                  }, {
                    range: '3–5 "No" answers',
                    label: 'Moderate risk',
                    detail: 'Gaps exist that are worth closing before they surface in an audit.',
                    active: noCount >= 3 && noCount <= 5
                  }, {
                    range: '6+ "No" answers',
                    label: 'High risk',
                    detail: 'Manual, undocumented sync at this scale is a matter of when, not if, something breaks or an ATO gets questioned.',
                    active: noCount >= 6
                  }].map(s => <div key={s.label} className={`rounded-lg p-4 border transition-colors ${s.active ? 'bg-nrs-orange/20 border-nrs-orange' : 'bg-white/10 border-white/15'}`} data-id="element-1260" data-mp="7ly7388axoc2">
                          <div className="flex flex-wrap items-baseline gap-2 mb-1" data-id="element-1261" data-mp="10b5buoycna5r">
                            <span className="font-heading font-bold text-nrs-orange text-sm" data-id="element-1262" data-mp="lminjxqqv82e">
                              {s.range}:
                            </span>
                            <span className="font-heading font-bold text-white text-sm" data-id="element-1263" data-mp="qjsp2brera6l">
                              {s.label}.
                            </span>
                          </div>
                          <p className="text-nrs-light text-sm leading-relaxed" data-id="element-1264" data-mp="1j37olp7ve6aq">
                            {s.detail}
                          </p>
                        </div>)}
                    </div>
                  </div>

                  <button type="button" onClick={handleReset} className="w-full py-3 border-2 border-gray-300 text-gray-600 font-semibold rounded-xl hover:border-nrs-blue hover:text-nrs-blue transition-colors" data-id="element-1265" data-mp="1jx6y7bjh2ueq">
                    Retake Checklist
                  </button>
                </motion.div>}
            </AnimatePresence>
          </div>
        </div>
      </article>

      {/* CLOSING CTA */}
      <section className="section-navy" data-id="element-1266" data-mp="1097n1zkzxl8s">
        <div className="max-w-[1200px] mx-auto px-5 md:px-10 lg:px-15 text-center" data-id="element-1267" data-mp="10a972tjzpi2n">
          <h2 className="text-3xl font-bold text-white mb-4" data-id="element-1268" data-mp="w8fdf587pebz">
            Want a Second Opinion on Your Score?
          </h2>
          <div className="h-1 w-16 bg-nrs-orange mx-auto rounded-full mb-8" data-id="element-1269" data-mp="r8002dnxk6u0"></div>
          <p className="text-xl text-nrs-light mb-8 max-w-2xl mx-auto" data-id="element-1270" data-mp="mahqcl239nky">
            Schedule a free 30-minute cross-instance governance assessment. No
            commitment required.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-1271" data-mp="32utrxzniwqf2">
            <a href="mailto:info@newriversystems.com" className="btn-primary" data-id="element-1272" data-mp="37ug4s7zfw4nc">
              Schedule a Free Assessment
            </a>
            <Link to="/services/servicenow/calculator" className="btn-secondary" data-id="element-1273" data-mp="3bv9vf949nxjx">
              Try the Risk Calculator
            </Link>
          </div>
        </div>
      </section>
    </div>;
}